import { componentWrapper } from "../../utilities";
import { Icon } from "../icon";
import { Alert as MantineAlert } from "@mantine/core";
import { faInfoCircle, faExclamationTriangle, faCheckCircle, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import styles from "./alert.module.scss";
import { AlertProps, AlertTypeProps, AlertFactory } from ".";

/**
 * Alert component for displaying important messages to users.
 * @param {React.ReactNode} props.children - The content to display in the alert.
 * @param {IconDefinition} props.icon - Optional icon to display in the alert.
 * @returns {JSX.Element} The Alert component.
 */
const Alert = componentWrapper<AlertFactory, AlertProps>("Alert", ({ children, icon, ...otherProps }, ref) => {
    return (
        <MantineAlert
            ref={ref}
            icon={<Icon icon={icon} />}
            classNames={{
                root: styles["root"],
                wrapper: styles["wrapper"],
                body: styles["body"],
                title: styles["title"],
                label: styles["label"],
                message: styles["message"],
                icon: styles["icon"],
                closeButton: styles["closeButton"],
            }}
            {...otherProps}
        >
            {children}
        </MantineAlert>
    );
});

/**
 * Info variant of the Alert component with a blue color scheme.
 * @returns {JSX.Element} The AlertInfo component.
 */
const AlertInfo = componentWrapper<AlertFactory, AlertTypeProps>("Alert", ({ icon = faInfoCircle, ...props }, ref) => {
    return <Alert ref={ref} icon={icon} {...props} color="var(--surface-primary-solid-color)" data-type="info" />;
});

/**
 * Success variant of the Alert component with a green color scheme.
 * @returns {JSX.Element} The AlertSuccess component.
 */
const AlertSuccess = componentWrapper<AlertFactory, AlertTypeProps>(
    "Alert",
    ({ icon = faCheckCircle, ...props }, ref) => {
        return <Alert ref={ref} icon={icon} data-type="success" {...props} color="var(--surface-success-solid)" />;
    }
);

/**
 * Warning variant of the Alert component with a yellow color scheme.
 * @returns {JSX.Element} The AlertWarning component.
 */
const AlertWarning = componentWrapper<AlertFactory, AlertTypeProps>(
    "Alert",
    ({ icon = faExclamationTriangle, ...props }, ref) => {
        return (
            <Alert ref={ref} icon={icon} data-type="warning" {...props} color="var(--surface-warning-solid-color)" />
        );
    }
);

/**
 * Error variant of the Alert component with a red color scheme.
 * @returns {JSX.Element} The AlertError component.
 */
const AlertError = componentWrapper<AlertFactory, AlertTypeProps>(
    "Alert",
    ({ icon = faTimesCircle, ...props }, ref) => {
        return <Alert ref={ref} icon={icon} data-type="error" {...props} color="var(--surface-error-solid)" />;
    }
);

Alert.Info = AlertInfo;
Alert.Success = AlertSuccess;
Alert.Warning = AlertWarning;
Alert.Error = AlertError;

export { Alert, AlertInfo, AlertSuccess, AlertWarning, AlertError };
